import * as THREE from 'three';
import { SEA, getFoamMaterial } from './style.js';

// Boats fire torpedoes along the surface; harpoon skiffs fire cannon, and
// anything else, or a shot up at an aircraft, flies through the air.
const SURFACE_SHOOTERS = new Set(['player', 'tank', 'super_tank']);

function chooseStyle(shooterType, start, end) {
  if (shooterType === 'rocket_launcher') return 'shell';
  if (shooterType === 'airplane') return 'rocket';
  if (shooterType === 'super_helicopter') return 'bomb';
  // The server raises both ends of a shot above the ground; a level, low shot runs on the water
  const surface = end && Math.abs(end.y - start.y) < 1.5 && end.y < 4;
  if (SURFACE_SHOOTERS.has(shooterType) && surface) return 'torpedo';
  return 'ball';
}

// Height of the shot above the shooter's ground (see the server's projectile spawn)
const SHOT_HEIGHT = 1.2;
const RIPPLE_SPACING = 1.4;
const RIPPLE_LIFE = 1300;

// Geometries are shared by every projectile. Rounds are modelled along +Z so
// Object3D.lookAt points them down the flight path.
const GEOMETRY = {
  ball: new THREE.IcosahedronGeometry(0.3, 1),
  // Tapered tracer trailing behind the round, from the origin back along -Z
  tracer: new THREE.CylinderGeometry(0.16, 0.02, 2.6, 8, 1, true).rotateX(Math.PI / 2).translate(0, 0, -1.3),
  // Rope from the origin back along -Z, scaled to the distance travelled
  fire: new THREE.IcosahedronGeometry(0.6, 1),
  bomb: new THREE.SphereGeometry(0.3, 10, 6).scale(1, 1, 1.9),
  fin: new THREE.BoxGeometry(0.7, 0.04, 0.22).translate(0, 0, -0.55),
  rocketBody: new THREE.CylinderGeometry(0.11, 0.11, 1.0, 8).rotateX(Math.PI / 2),
  rocketNose: new THREE.ConeGeometry(0.11, 0.3, 8).rotateX(Math.PI / 2).translate(0, 0, 0.65),
  flame: new THREE.ConeGeometry(0.16, 0.9, 8, 1, true).rotateX(-Math.PI / 2).translate(0, 0, -0.95),
  // Torpedo, centred on the waterline: hull, rounded nose, tapered tail
  torpedoBody: new THREE.CylinderGeometry(0.32, 0.32, 2.2, 14).rotateX(Math.PI / 2),
  torpedoNose: new THREE.SphereGeometry(0.32, 14, 8).scale(1, 1, 1.7).translate(0, 0, 1.1),
  torpedoTail: new THREE.ConeGeometry(0.32, 0.8, 14).rotateX(-Math.PI / 2).translate(0, 0, -1.5),
  torpedoBand: new THREE.TorusGeometry(0.33, 0.07, 6, 18),
  torpedoFin: new THREE.BoxGeometry(0.1, 0.18, 1.5).translate(0, 0.33, -0.2),
  propHub: new THREE.CylinderGeometry(0.08, 0.08, 0.2, 8).rotateX(Math.PI / 2),
  propBlade: new THREE.BoxGeometry(0.08, 0.42, 0.05).translate(0, 0.21, 0),
  bowWave: new THREE.RingGeometry(0.3, 0.75, 14, 1, 0, Math.PI).rotateX(-Math.PI / 2),
  ripple: new THREE.RingGeometry(0.75, 1.0, 28, 1).rotateX(-Math.PI / 2),
  glowPlane: new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2),
  splashDrop: new THREE.IcosahedronGeometry(0.16, 0)
};

// A V of churned foam spreading back from the nose, plus a straight wash
// behind the tail. aEdge runs 0 at the front to 1 at the far end, so the
// shared foam shader thins it out behind the torpedo.
const WAKE_GEOMETRY = (() => {
  const positions = [];
  const edge = [];
  const indices = [];
  const strip = (from, to, w0, w1) => {
    const steps = 8;
    const start = positions.length / 3;
    const dir = new THREE.Vector2(to[0] - from[0], to[1] - from[1]).normalize();
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = from[0] + (to[0] - from[0]) * t;
      const z = from[1] + (to[1] - from[1]) * t;
      const w = w0 + (w1 - w0) * t;
      positions.push(x - dir.y * w, 0, z + dir.x * w, x + dir.y * w, 0, z - dir.x * w);
      edge.push(t, t);
      if (i < steps) {
        const k = start + i * 2;
        indices.push(k, k + 2, k + 1, k + 1, k + 2, k + 3);
      }
    }
  };
  strip([0.25, 1.4], [2.0, -6.5], 0.18, 0.6);
  strip([-0.25, 1.4], [-2.0, -6.5], 0.18, 0.6);
  strip([0, -1.6], [0, -8], 0.35, 0.9);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('aEdge', new THREE.Float32BufferAttribute(edge, 1));
  geometry.setIndex(indices);
  return geometry;
})();

const WHITE_WATER = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9, depthWrite: false, side: THREE.DoubleSide });

const IRON = new THREE.MeshStandardMaterial({ color: 0x2c2c30, roughness: 0.45, metalness: 0.6 });
const STEEL = new THREE.MeshStandardMaterial({ color: 0x9aa0a8, roughness: 0.35, metalness: 0.7, flatShading: true });
const FIRE = new THREE.MeshBasicMaterial({ color: 0xff8a2a, transparent: true, opacity: 0.8, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false });
const FLAME = new THREE.MeshBasicMaterial({ color: 0xffc060, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false, side: THREE.DoubleSide });

const tracerMaterials = new Map();
function tracerMaterial(color) {
  const key = color.getHexString();
  if (!tracerMaterials.has(key)) {
    // Normal blending: additive glows wash out to white over sunlit water
    tracerMaterials.set(key, new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      toneMapped: false
    }));
  }
  return tracerMaterials.get(key);
}

let haloTexture = null;
function getHaloTexture() {
  if (haloTexture) return haloTexture;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 64;
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.25, 'rgba(255,255,255,0.7)');
  gradient.addColorStop(0.6, 'rgba(255,255,255,0.15)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 64, 64);
  haloTexture = new THREE.CanvasTexture(canvas);
  return haloTexture;
}

// Puff trail: soft round points that grow and fade with age. Smoke is drawn
// normally; embers add light.
function trailMaterial(color, size, additive) {
  return new THREE.ShaderMaterial({
    uniforms: { uColor: { value: new THREE.Color(color) }, uSize: { value: size }, uGrow: { value: additive ? 0.2 : 1.6 } },
    transparent: true,
    depthWrite: false,
    blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
    vertexShader: /* glsl */`
      uniform float uSize;
      uniform float uGrow;
      attribute float aAge;
      varying float vAge;
      void main() {
        vAge = aAge;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = uSize * (1.0 + aAge * uGrow) * (300.0 / max(-mv.z, 1.0));
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */`
      uniform vec3 uColor;
      varying float vAge;
      void main() {
        float d = length(gl_PointCoord - 0.5);
        float a = smoothstep(0.5, 0.1, d) * (1.0 - vAge) * 0.75;
        gl_FragColor = vec4(uColor, a);
        #include <colorspace_fragment>
      }
    `
  });
}

export class Projectile {
  // shooterType is the shooter's unit type ('player', 'tank', 'sniper', ...) or 'turret'.
  constructor(scene, projectileData, color, shooterType = null) {
    this.scene = scene;
    this.data = projectileData;
    this.shooterType = shooterType;
    this.style = chooseStyle(shooterType, projectileData.position, projectileData.endPos);
    this.isTorpedo = this.style === 'torpedo';
    // Torpedoes run on the waterline (or along an island top, if fired from one)
    const ground = projectileData.position.y - SHOT_HEIGHT;
    this.waterY = ground > 1 ? ground : 0;
    this.ripples = [];
    this.lastRipple = null;
    this.color = new THREE.Color(color);
    this.mesh = null;
    this.trails = [];
    this.trailPositions = [];
    this.maxTrailLength = 14;
    this.create();
  }

  create() {
    this.mesh = new THREE.Group();
    const tracer = tracerMaterial(this.color);

    if (this.isTorpedo) {
      this.createTorpedo(tracer);
    } else if (this.style === 'shell') {
      this.mesh.add(new THREE.Mesh(GEOMETRY.ball, IRON));
      this.fire = new THREE.Mesh(GEOMETRY.fire, FIRE);
      this.mesh.add(this.fire);
      this.mesh.add(new THREE.Mesh(GEOMETRY.tracer, tracer));
      this.addHalo(2.6, 0.85);
      this.addTrail(0xffa040, 0.7, true);
      this.addTrail(0x8a8580, 0.8, false);
    } else if (this.style === 'bomb') {
      this.mesh.add(new THREE.Mesh(GEOMETRY.bomb, IRON));
      for (const rz of [Math.PI / 4, -Math.PI / 4]) {
        const fin = new THREE.Mesh(GEOMETRY.fin, IRON);
        fin.rotation.z = rz;
        this.mesh.add(fin);
      }
      const band = new THREE.Mesh(new THREE.TorusGeometry(0.3, 0.05, 4, 12), tracer);
      this.mesh.add(band);
      this.ownGeometry = band.geometry;
      this.mesh.add(new THREE.Mesh(GEOMETRY.tracer, tracer));
      this.mesh.scale.setScalar(1.3);
      this.addHalo(2.2, 0.8);
      this.addTrail(0xd8d4cc, 0.8, false);
    } else if (this.style === 'rocket') {
      this.mesh.add(new THREE.Mesh(GEOMETRY.rocketBody, IRON));
      this.mesh.add(new THREE.Mesh(GEOMETRY.rocketNose, tracer));
      this.flame = new THREE.Mesh(GEOMETRY.flame, FLAME);
      this.mesh.add(this.flame);
      this.addHalo(1.6, 0.8, -0.6);
      this.addTrail(0xf0eee8, 0.6, false);
    } else {
      // Cannonball: dark iron with a team-coloured tracer and a smoke trail
      this.mesh.add(new THREE.Mesh(GEOMETRY.ball, IRON));
      this.mesh.add(new THREE.Mesh(GEOMETRY.tracer, tracer));
      if (this.shooterType === 'super_tank') this.mesh.scale.setScalar(1.35);
      this.addHalo(1.7, 0.8);
      this.addTrail(0xcfcac2, 0.55, false);
    }

    this.flicker = Math.random() * 100;
    this.updatePosition(this.data.position);
    this.orient();
    this.scene.add(this.mesh);
  }

  // A dark iron torpedo running half-submerged, team bands and a team fin
  // showing above the water, foam V-wake, bow wave and a team glow beneath.
  createTorpedo(tracer) {
    const hull = new THREE.Group();
    // Sit the hull a little below the waterline, so about 60% is under water
    hull.position.y = -0.07;
    hull.add(new THREE.Mesh(GEOMETRY.torpedoBody, IRON));
    hull.add(new THREE.Mesh(GEOMETRY.torpedoNose, IRON));
    hull.add(new THREE.Mesh(GEOMETRY.torpedoTail, IRON));
    hull.add(new THREE.Mesh(GEOMETRY.torpedoFin, tracer));
    for (const z of [0.6, -0.7]) {
      const band = new THREE.Mesh(GEOMETRY.torpedoBand, tracer);
      band.position.z = z;
      hull.add(band);
    }
    this.prop = new THREE.Group();
    this.prop.position.z = -1.95;
    this.prop.add(new THREE.Mesh(GEOMETRY.propHub, IRON));
    for (let i = 0; i < 4; i++) {
      const blade = new THREE.Mesh(GEOMETRY.propBlade, STEEL);
      blade.rotation.z = (i / 4) * Math.PI * 2;
      this.prop.add(blade);
    }
    hull.add(this.prop);
    this.mesh.add(hull);

    const wake = new THREE.Mesh(WAKE_GEOMETRY, getFoamMaterial());
    wake.position.y = 0.06;
    wake.renderOrder = 2;
    this.mesh.add(wake);
    this.bow = new THREE.Mesh(GEOMETRY.bowWave, WHITE_WATER);
    this.bow.position.set(0, 0.07, 1.75);
    this.bow.renderOrder = 2;
    this.mesh.add(this.bow);

    // A wash of team colour on the water so incoming torpedoes stand out
    this.glow = new THREE.Mesh(GEOMETRY.glowPlane, new THREE.MeshBasicMaterial({
      map: getHaloTexture(), color: this.color, transparent: true, opacity: 0.6, depthWrite: false, toneMapped: false
    }));
    this.glow.scale.set(2.6, 1, 7);
    this.glow.position.set(0, 0.04, -1);
    this.glow.renderOrder = 1;
    this.mesh.add(this.glow);
    this.addHalo(1.8, 0.85);
    this.halo.position.y = 0.7;
  }

  // Rings spreading out behind the torpedo every metre or so, fading as they grow
  updateRipples(now) {
    const p = this.mesh.position;
    if (!this.lastRipple || this.lastRipple.distanceTo(p) > RIPPLE_SPACING) {
      const ring = new THREE.Mesh(GEOMETRY.ripple, new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.7, depthWrite: false }));
      ring.position.set(p.x, this.waterY + 0.05, p.z);
      ring.userData.born = now;
      ring.renderOrder = 1;
      this.scene.add(ring);
      this.ripples.push(ring);
      this.lastRipple = p.clone();
    }
    this.ripples = this.ripples.filter(ring => {
      const t = (now - ring.userData.born) / RIPPLE_LIFE;
      if (t >= 1) {
        this.scene.remove(ring);
        ring.material.dispose();
        return false;
      }
      ring.scale.setScalar(0.6 + t * 2.4);
      ring.material.opacity = 0.7 * (1 - t);
      return true;
    });
  }

  // A team-coloured glow so every round is easy to spot against the water
  addHalo(size, opacity, z = 0) {
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
      map: getHaloTexture(),
      color: this.color,
      transparent: true,
      opacity: opacity * 0.75,
      depthWrite: false,
      toneMapped: false
    }));
    sprite.scale.setScalar(size);
    sprite.position.z = z;
    this.mesh.add(sprite);
    this.halo = sprite;
  }

  addTrail(color, size, additive) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(this.maxTrailLength * 3), 3));
    geometry.setAttribute('aAge', new THREE.BufferAttribute(new Float32Array(this.maxTrailLength), 1));
    const points = new THREE.Points(geometry, trailMaterial(color, size * this.mesh.scale.x, additive));
    points.frustumCulled = false;
    this.scene.add(points);
    this.trails.push(points);
  }

  // Point the round along its flight path (start -> end)
  orient() {
    const end = this.data.endPos;
    if (!end) return;
    const target = new THREE.Vector3(end.x, this.isTorpedo ? this.waterY : end.y, end.z);
    if (target.distanceToSquared(this.mesh.position) > 0.0001) {
      this.mesh.lookAt(target);
    }
  }

  updatePosition(position) {
    if (!this.mesh) return;
    if (this.isTorpedo) {
      // Follow the server along the surface, whatever height it reports
      this.mesh.position.set(position.x, this.waterY, position.z);
      const now = performance.now();
      this.prop.rotation.z = now * 0.03;
      this.bow.scale.setScalar(1 + Math.sin(now * 0.02) * 0.15);
      this.updateRipples(now);
      return;
    }
    this.mesh.position.set(position.x, position.y, position.z);

    const t = performance.now() * 0.02 + this.flicker;
    if (this.fire) this.fire.scale.setScalar(1 + Math.sin(t) * 0.15 + Math.sin(t * 2.3) * 0.08);
    if (this.flame) this.flame.scale.set(1, 1, 0.8 + Math.sin(t * 1.7) * 0.25);

    this.updateTrail(position);
  }

  updateTrail(position) {
    if (!this.trails.length) return;

    // Only record new points when the projectile has actually moved
    const last = this.trailPositions[this.trailPositions.length - 1];
    if (last && Math.abs(last.x - position.x) + Math.abs(last.y - position.y) + Math.abs(last.z - position.z) < 0.05) {
      return;
    }
    // Fill in between server ticks so the trail is a continuous plume
    if (last) {
      for (let s = 1; s < 3; s++) {
        const k = s / 3;
        this.trailPositions.push({ x: last.x + (position.x - last.x) * k, y: last.y + (position.y - last.y) * k, z: last.z + (position.z - last.z) * k });
      }
    }
    this.trailPositions.push({ x: position.x, y: position.y, z: position.z });
    while (this.trailPositions.length > this.maxTrailLength) this.trailPositions.shift();

    const count = this.trailPositions.length;
    for (const trail of this.trails) {
      const positions = trail.geometry.attributes.position;
      const ages = trail.geometry.attributes.aAge;
      for (let i = 0; i < count; i++) {
        const p = this.trailPositions[i];
        const age = count > 1 ? 1 - i / (count - 1) : 0;
        // Smoke drifts up a little as it ages
        positions.setXYZ(i, p.x, p.y + age * 0.3, p.z);
        ages.setX(i, age);
      }
      positions.needsUpdate = true;
      ages.needsUpdate = true;
      trail.geometry.setDrawRange(0, count);
    }
  }

  remove() {
    // Shared geometries and materials stay alive; only per-projectile resources are disposed
    if (this.isTorpedo && this.mesh) {
      splash(this.scene, this.mesh.position.clone());
      for (const ring of this.ripples) {
        this.scene.remove(ring);
        ring.material.dispose();
      }
      this.ripples = [];
      this.glow.material.dispose();
    }
    if (this.mesh) {
      this.scene.remove(this.mesh);
      if (this.halo) this.halo.material.dispose();
      if (this.ownGeometry) this.ownGeometry.dispose();
      this.mesh = null;
      this.halo = null;
      this.fire = null;
      this.flame = null;
    }
    for (const trail of this.trails) {
      this.scene.remove(trail);
      trail.geometry.dispose();
      trail.material.dispose();
    }
    this.trails = [];
  }
}

// A small splash where a torpedo ends its run: a spray of droplets and a ring
// of foam. It animates itself and cleans up after a moment.
function splash(scene, position) {
  const material = new THREE.MeshLambertMaterial({ color: 0xf2fcff, transparent: true, opacity: 0.95, flatShading: true });
  const ringMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.85, depthWrite: false });
  const group = new THREE.Group();
  const drops = [];
  for (let i = 0; i < 14; i++) {
    const drop = new THREE.Mesh(GEOMETRY.splashDrop, material);
    const a = Math.random() * Math.PI * 2;
    const out = 1 + Math.random() * 2.5;
    drop.userData.velocity = new THREE.Vector3(Math.cos(a) * out, 5 + Math.random() * 5, Math.sin(a) * out);
    drop.scale.setScalar(0.8 + Math.random() * 1.2);
    group.add(drop);
    drops.push(drop);
  }
  const ring = new THREE.Mesh(GEOMETRY.ripple, ringMaterial);
  ring.position.y = 0.06;
  group.add(ring);
  group.position.copy(position);
  scene.add(group);

  const start = performance.now();
  let last = start;
  const step = () => {
    const now = performance.now();
    const t = (now - start) / 700;
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    if (t >= 1 || !group.parent) {
      scene.remove(group);
      material.dispose();
      ringMaterial.dispose();
      return;
    }
    for (const drop of drops) {
      drop.position.addScaledVector(drop.userData.velocity, dt);
      drop.userData.velocity.y -= 25 * dt;
      drop.visible = drop.position.y > -0.1;
    }
    material.opacity = 0.95 * (1 - Math.max(0, t - 0.5) * 2);
    ring.scale.setScalar(1 + t * 3);
    ringMaterial.opacity = 0.85 * (1 - t);
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}
